import * as storage from '@groww-tech-pvt/web-storage';
import { STORAGE_CONSTANTS, STORAGE_KEY_CONSTANTS, STORAGE_TYPE } from './constant';
import { getStorage } from '@adapter/web-storage';
import webview from '@adapter/webview';

const WV = webview();

export function getRefreshToken() {
  const isEncrypted = WV.isActive ? false : true;

  return getStorage(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.REFRESH_TOKEN,
    isEncrypted
  );
}

export function clearParticularKeyStorage(storageType: string, key: string) {
  if (
    storageType === STORAGE_TYPE.COOKIE ||
    storageType === STORAGE_TYPE.LOCAL_COOKIE_STORAGE
  ) {
    clearSpecificCookiesWithParentDomain([key]);
  }
  storage.clearKeyFromStorage(key, storageType);
}


export function getOnboardingUITheme() {
  return getStorage(STORAGE_TYPE.LOCAL_STORAGE, STORAGE_KEY_CONSTANTS.UI_THEME, false);
}

/**
 * Check if the hostname is localhost or an IP address
 * @param hostname - The hostname to check
 * @returns true if hostname is localhost or IP address
 */
function isLocalhostOrIP(hostname: string): boolean {
  if (!hostname) return false;

  // Check for localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }

  // Check for IP address pattern (IPv4)
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Pattern.test(hostname)) {
    return true;
  }

  // Check for IPv6 localhost
  if (hostname === '[::1]' || hostname.startsWith('::')) {
    return true;
  }

  return false;
}

/**
 * Get SameSite and Secure attributes for cookie
 * @returns String with SameSite and Secure attributes
 */
function getSameSiteSecureAttrs(): string {
  // Check if we're on HTTPS or localhost
  const isSecure =
    typeof window !== 'undefined' &&
    (window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  if (isSecure) {
    return '; SameSite=Strict; Secure';
  }

  return '; SameSite=Lax';
}

/**
 * 
/**
 * Clear all cookies for the current domain and parent domain
 * Used during logout to ensure all cookies are removed across all subdomains
 *
 * @internal
 */
export function clearAllCookiesWithParentDomain() {
  if (typeof document === 'undefined') {
    return;
  }

  const parentDomain = getCookieDomain();
  const pastDate = 'Thu, 01 Jan 1970 00:00:01 GMT';
  const sameSiteSecure = getSameSiteSecureAttrs();

  // Get all cookies from document.cookie
  const cookies = document.cookie.split(';');

  cookies.forEach(cookie => {
    const key = cookie.split('=')[0].trim();

    if (!key) return;

    // Clear with parent domain (e.g., .groww.in)
    if (parentDomain) {
      document.cookie = `${key}=; Domain=${parentDomain}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
    }

    // Clear without domain attribute (current domain only)
    document.cookie = `${key}=; Path=/; Expires=${pastDate}${sameSiteSecure}`;

    // Clear with current hostname as domain (e.g., .subdomain.groww.in)
    if (typeof window !== 'undefined' && window.location.hostname) {
      const currentHost = window.location.hostname;
      if (!isLocalhostOrIP(currentHost)) {
        document.cookie = `${key}=; Domain=.${currentHost}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
      }
    }
  });
}
/**
 * Get the parent domain for cookie clearing
 * For example: subdomain.groww.in -> .groww.in
 * @returns The parent domain with leading dot, or null if not applicable
 */
function getCookieDomain(): string | null {
  if (typeof window === 'undefined' || !window.location.hostname) {
    return null;
  }

  const hostname = window.location.hostname;

  // Don't set domain for localhost or IP addresses
  if (isLocalhostOrIP(hostname)) {
    return null;
  }

  // Split hostname into parts
  const parts = hostname.split('.');

  // If we have at least 2 parts (e.g., subdomain.domain.com), return parent domain
  if (parts.length >= 2) {
    // Get the last two parts (e.g., domain.com)
    const parentDomain = parts.slice(-2).join('.');
    return `.${parentDomain}`;
  }

  // For single-part domains (e.g., localhost), return null
  return null;
}

/**
 * Clear specific cookies by key names across all domains and subdomains
 * @param cookieKeys - Array of cookie keys to clear
 */
export function clearSpecificCookiesWithParentDomain(
  cookieKeys: string[],
  withCurrentDomain = true
) {
  if (typeof document === 'undefined') {
    return;
  }

  const parentDomain = getCookieDomain();
  const pastDate = 'Thu, 01 Jan 1970 00:00:01 GMT';
  const sameSiteSecure = getSameSiteSecureAttrs();

  cookieKeys.forEach(key => {
    if (!key) return;

    // Clear with parent domain (e.g., .groww.in)
    if (parentDomain) {
      document.cookie = `${key}=; Domain=${parentDomain}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
    }

    // Clear without domain attribute (current domain only)
    if (withCurrentDomain) {
      document.cookie = `${key}=; Path=/; Expires=${pastDate}${sameSiteSecure}`;
    }

    // Clear with current hostname as domain (e.g., .subdomain.groww.in)
    if (typeof window !== 'undefined' && window.location.hostname) {
      const currentHost = window.location.hostname;
      if (!isLocalhostOrIP(currentHost)) {
        document.cookie = `${key}=; Domain=.${currentHost}; Path=/; Expires=${pastDate}${sameSiteSecure}`;
      }
    }
  });
}